<template>
  <h3>Change Password</h3>
  <div class="regular-form-width">
    <password-strength-checker/>

    <atom-info-box v-if="validationError.message" priority="danger" role="alert">
      {{ validationError.message }}
    </atom-info-box>

    <atom-info-box v-else-if="resetPasswordResult?.message" priority="danger" role="alert">
      {{ resetPasswordResult?.message }}
    </atom-info-box>

    <atom-text-input id="current-password" v-model="currentPassword" type="password" label="Current password"
                     :error-message="validationError.currentPassword"/>

    <atom-text-input id="password" v-model="password" type="password" label="New password"
                     :error-message="validationError.password"/>

    <atom-text-input id="password-confirm" v-model="passwordConfirmation" type="password" label="Confirm new password"
                     :error-message="validationError.passwordConfirmation"/>

    <atom-button label="Set password" type="primary" :disabled="loading" @click="changePassword"/>
  </div>
</template>

<script lang="ts">
import type { ResetPasswordResult } from "authentication/js/api/password.api";
import { useVuelidate } from "@vuelidate/core";
import { required, sameAs } from "@vuelidate/validators";
import PasswordStrengthChecker from "authentication/components/login/components/PasswordStrengthChecker.vue";
import { changePassword } from "authentication/js/api/password.api";
import { useChangePasswordStore } from "authentication/js/composable/change-password.store";
import AtomButton from "core/components/atom/button/AtomButton.vue";
import AtomInfoBox from "core/components/atom/infoBox/AtomInfoBox.vue";
import AtomTextInput from "core/components/atom/textInput/AtomTextInput.vue";
import { showChangesSavedFlash, showValidationFlash } from "core/js/flashmessage/flash-message";
import { getFirstValidationError } from "core/js/validation/validation-messages";
import { template } from "lodash";
import { storeToRefs } from "pinia";
import { defineComponent } from "vue";

const emptyValidationError = {
  currentPassword: "",
  password: "",
  passwordConfirmation: "",
  message: "",
};

export default defineComponent({
  components: { AtomInfoBox, AtomButton, AtomTextInput, PasswordStrengthChecker },
  setup() {
    const {
      password,
      passwordConfirmation,
      passwordMeetsSecurityCriteria,
    } = storeToRefs(useChangePasswordStore());

    return {
      password,
      passwordConfirmation,
      passwordMeetsSecurityCriteria,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      loading: false,
      currentPassword: "",
      changePasswordStatus: "",
      resetPasswordResult: {} as ResetPasswordResult,
      validationError: emptyValidationError,
    };
  },
  validations() {
    return {
      currentPassword: { required },
      password: { required },
      passwordConfirmation: {
        required,
        sameAsPassword: sameAs(this.password),
      },
    };
  },
  methods: {
    async changePassword() {
      this.loading = true;
      this.resetPasswordResult = {} as ResetPasswordResult;
      this.validationError = emptyValidationError;

      if (await this.v$.$validate() && this.passwordMeetsSecurityCriteria) {
        this.resetPasswordResult = await changePassword(this.currentPassword, this.password);
        if (this.resetPasswordResult?.resetPasswordStatus === "SUCCESS") {
          showChangesSavedFlash();
          this.clearInputs();
        } else if (this.resetPasswordResult?.message) {
          showValidationFlash();
        }
      } else {
        this.displayValidationErrors();
      }

      this.loading = false;
    },
    clearInputs() {
      this.currentPassword = "";
      this.password = "";
      this.passwordConfirmation = "";
    },
    displayValidationErrors() {
      this.validationError = {
        currentPassword: getFirstValidationError(
          this.v$.currentPassword,
          "Current password",
          { required: template("Enter your current password") },
        ),
        password: getFirstValidationError(
          this.v$.password,
          "New password",
          {
            required: template("Enter a new password"),
          },
        ),
        passwordConfirmation: getFirstValidationError(
          this.v$.passwordConfirmation,
          "Confirm new password",
          {
            required: template("Enter your new password again"),
            sameAsPassword: template("The password confirmation does not match"),
          },
        ),
        message: "",
      };

      if (!this.passwordMeetsSecurityCriteria && !this.validationError.password) {
        this.validationError = {
          ...this.validationError,
          message: "The password entered does not conform to the password policy",
        };
      }
    },
  },
});
</script>
